import React from "react";

import { Box, Typography, Avatar } from "@mui/material";

import Waveform from "../Waveform";
import BackgroundLayer from "./BackgroundLayer";
import { AudiogramEditorState } from "../../types";

interface DarkMinimalProps {
  state: AudiogramEditorState;
  scale?: number;
  audioLevels?: number[];
  isPlaying?: boolean;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function DarkMinimal({
  state,
  scale = 1,
  audioLevels,
  isPlaying,
}: DarkMinimalProps) {
  const {
    quote,
    fontFamily,
    fontSize,
    textColor,
    showName,
    guestName,
    accentColor,
    waveformStyle,
    waveformColor,
    waveformHeight,
    animated,
    showEpisodeNumber,
    episodeNumber,
    backgroundImage,
    backgroundOverlay,
    backgroundFit,
    showGuestAvatar,
    showDate,
    date,
  } = state;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: "#111111",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        p: 3 * scale,
        boxSizing: "border-box",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {backgroundImage && (
        <BackgroundLayer
          image={backgroundImage}
          overlay={backgroundOverlay}
          overlayColor="#111111"
          fit={backgroundFit}
        />
      )}

      {/* Content layer — above background */}
      <Box
        sx={{
          position: "relative",
          zIndex: 3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          flex: 1,
        }}
      >
        {/* Show name — top left */}
        <Typography
          sx={{
            fontSize: 10 * scale,
            fontWeight: 600,
            color: "rgba(255,255,255,0.4)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            lineHeight: 1,
          }}
        >
          {showName}
        </Typography>

        {/* Quote — centered */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 1 * scale,
            py: 2 * scale,
          }}
        >
          <Typography
            sx={{
              color: textColor,
              fontSize: fontSize * scale,
              fontWeight: 500,
              fontFamily,
              textAlign: "center",
              lineHeight: 1.6,
              letterSpacing: "-0.01em",
              display: "-webkit-box",
              WebkitLineClamp: 12,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {quote}
          </Typography>
        </Box>

        {/* Waveform — bottom third */}
        <Box sx={{ px: 1 * scale, mb: 2 * scale }}>
          <Waveform
            style={waveformStyle}
            color={waveformColor || accentColor}
            height={waveformHeight}
            animated={animated}
            barCount={56}
            levels={audioLevels}
            playing={isPlaying}
          />
        </Box>

        {/* Divider */}
        <Box
          sx={{
            height: "1px",
            backgroundColor: "rgba(255,255,255,0.1)",
            mx: 1 * scale,
            mb: 1.5 * scale,
          }}
        />

        {/* Guest info — bottom */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5 * scale,
            px: 1 * scale,
          }}
        >
          {showGuestAvatar !== false && (
            <Avatar
              sx={{
                width: 28 * scale,
                height: 28 * scale,
                fontSize: 11 * scale,
                backgroundColor: "#2A2A2A",
                color: "rgba(255,255,255,0.6)",
                fontWeight: 700,
              }}
            >
              {getInitials(guestName || "G")}
            </Avatar>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                color: "#FFFFFF",
                fontSize: 12 * scale,
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              {guestName}
            </Typography>
            {showEpisodeNumber && episodeNumber && (
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.4)",
                  fontSize: 9 * scale,
                  fontWeight: 500,
                }}
              >
                Episode {episodeNumber}
              </Typography>
            )}
          </Box>
          {showDate && date && (
            <Typography
              sx={{
                color: "rgba(255,255,255,0.4)",
                fontSize: 10 * scale,
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              {date}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
